// 📄 services/busquedaInteligenteService.ts
//
// "Búsqueda inteligente con IA" (2026-08-20, Fase 4) — arma el contexto
// (notas de SAP propias + compartidas del equipo + casos de prueba de los
// proyectos consultados) y se lo manda a la Cloud Function
// `busquedaInteligente` (Gemini + fallback Claude, ver
// functions/callable/busquedaInteligente.js). Todo el armado de texto
// pasa por acá para no duplicar el formateo en el componente de UI.
import { httpsCallable } from 'firebase/functions';
import { functionsClient } from '../firebase';
import { getNotasSap } from './miEspacioService.ts';
import { getNotasSapCompartidas } from './miEspacioCompartidoService.ts';
import { getCasosPrueba } from './casoPruebaService.ts';
import { etiquetaContexto } from '../pages/MiEspacio/miEspacioContexto.ts';

interface ArmarContextoParams {
  uid: string;
  contextosConsulta: (string | null)[];
  proyectosPorId: Record<string, string>;
}

/**
 * Junta las 3 fuentes en un solo texto plano para el prompt — notas
 * propias (global + proyectos consultados), notas COMPARTIDAS del/los
 * proyecto(s) consultados (todo el equipo, no solo lo del usuario), y
 * casos de prueba de esos mismos proyectos (con énfasis en los Fallidos,
 * que son los que realmente tienen "causa/solución" útil para responder
 * "¿ya resolvimos esto antes?").
 */
export const armarContextoBusqueda = async ({ uid, contextosConsulta, proyectosPorId }: ArmarContextoParams): Promise<string> => {
  const projectIds = contextosConsulta.filter((c): c is string => c !== null);

  const [notasPropias, notasCompartidasPorProyecto, casosPorProyecto] = await Promise.all([
    getNotasSap(uid, contextosConsulta),
    Promise.all(projectIds.map((pid) => getNotasSapCompartidas(pid).catch(() => []))),
    Promise.all(projectIds.map((pid) => getCasosPrueba(pid).catch(() => [])))
  ]);

  const bloques: string[] = [];

  notasPropias.forEach((n) => {
    bloques.push(
      `[Nota de SAP — ${etiquetaContexto(n.projectId, proyectosPorId)}]\n` +
      `Título: ${n.titulo}\n` +
      (n.numeroONota ? `Nota/Número: ${n.numeroONota}\n` : '') +
      `Módulo: ${n.modulo}\n` +
      `Causa: ${n.causa}\n` +
      `Solución: ${n.solucion}`
    );
  });

  notasCompartidasPorProyecto.forEach((notas, i) => {
    const nombreProyecto = proyectosPorId[projectIds[i]] || 'Proyecto';
    notas.forEach((n) => {
      bloques.push(
        `[Nota de SAP compartida por el equipo — ${nombreProyecto}, autor: ${n.autorEmail}]\n` +
        `Título: ${n.titulo}\n` +
        `Módulo: ${n.modulo}\n` +
        `Causa: ${n.causa}\n` +
        `Solución: ${n.solucion}`
      );
    });
  });

  casosPorProyecto.forEach((casos, i) => {
    const nombreProyecto = proyectosPorId[projectIds[i]] || 'Proyecto';
    casos.forEach((c) => {
      if (c.estado === 'Fallido' && c.detalleFallo) {
        bloques.push(
          `[Caso de prueba FALLIDO — ${nombreProyecto}]\n` +
          `Escenario: ${c.nombre}\n` +
          `Tipo de error: ${c.detalleFallo.tipoError}\n` +
          `Qué falló: ${c.detalleFallo.descripcion}`
        );
      } else if (c.estado === 'Aprobado') {
        bloques.push(`[Caso de prueba APROBADO — ${nombreProyecto}] Escenario: ${c.nombre}`);
      }
    });
  });

  return bloques.join('\n\n');
};

export const preguntarBusquedaInteligente = async (query: string, contexto: string): Promise<{ text: string; generatedBy: string }> => {
  const fn = httpsCallable<{ query: string; contexto: string }, { text: string; generatedBy: string }>(functionsClient, 'busquedaInteligente');
  const result = await fn({ query, contexto });
  return result.data;
};
